import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { normalizeError } from "../../helpers/request";

/** Pass a stable, module-level createCrudModule result, registered in the store. */
export default function useCrud(module) {
  const dispatch = useDispatch();
  const store = useStore();
  const state = useSelector(module.selectState);
  const pending = useRef({});
  const mounted = useRef(false);
  const [notification, setNotification] = useState({ open: false, msg: "", type: "success" });

  useEffect(() => {
    mounted.current = true;
    const callbacks = pending.current;
    return () => {
      mounted.current = false;
      Object.keys(callbacks).forEach(key => delete callbacks[key]);
    };
  }, [module]);

  useEffect(() => {
    Object.entries(pending.current).forEach(([operation, entry]) => {
      const result = state[operation];
      if (!result || result.requestId !== entry.id) {
        delete pending.current[operation];
        return;
      }
      if (result.loading) return;
      delete pending.current[operation];
      if (result.status === "error") {
        setNotification({ open: true, msg: result.error.message, type: "error" });
      } else if (result.status === "success") {
        if (entry.options.successMessage) {
          setNotification({ open: true, msg: entry.options.successMessage, type: "success" });
        }
        // A page callback failure must not escape the effect or change API status.
        Promise.resolve().then(() => {
          if (mounted.current) return entry.options.onSuccess?.(result.data);
        }).catch(error => {
          if (mounted.current) setNotification({ open: true, msg: normalizeError(error).message, type: "error" });
        });
      }
    });
  }, [state]);

  const run = useCallback((operation, params, options = {}) => {
    if (!Object.prototype.hasOwnProperty.call(module.actionTypes, operation)) {
      throw new Error(`Unknown CRUD operation: ${operation}`);
    }
    if (!store.getState()[module.stateKey]) throw new Error(`Register the ${module.stateKey} reducer before using useCrud.`);
    const action = module.actions[operation](params);
    pending.current[operation] = { id: action.meta.requestId, options };
    if (!options.preserveNotification) setNotification({ open: false, msg: "", type: "success" });
    dispatch(action);
    return action.meta.requestId;
  }, [dispatch, module, store]);

  const operations = useMemo(() => Object.fromEntries(Object.keys(module.actionTypes).map(operation => [
    operation, (params, options) => run(operation, params, options),
  ])), [module, run]);

  const reset = useCallback(operation => dispatch(module.actions.reset(operation)), [dispatch, module]);

  return {
    ...operations, state, loading: Object.values(state).some(result => result.loading),
    notification, setNotification,
    reset,
  };
}
